import { type ApiEnvelope, api, unwrap } from "@/lib/api";
import type { AuthUser, MfaMethod } from "@/stores/auth.store";

export interface UpdateMePayload {
	firstName?: string;
	lastName?: string;
	phone?: string;
	photoUrl?: string;
	lang?: string;
	timezone?: string;
}

export interface Session {
	id: string;
	userAgent: string | null;
	ip: string | null;
	lastActivityAt: string;
	expiresAt: string;
	createdAt: string;
	isCurrent: boolean;
}

export interface MfaMethodFull {
	id: string;
	type: "totp" | "sms" | "email" | "whatsapp";
	verified: boolean;
	isDefault: boolean;
	label: string | null;
	createdAt: string;
}

export interface TotpSetup {
	setupId: string;
	secret: string;
	qrSvg: string;
	otpauthUrl: string;
}

export const meApi = {
	get: () => unwrap<AuthUser>(api.get<ApiEnvelope<AuthUser>>("/me")),

	update: (payload: UpdateMePayload) =>
		unwrap<AuthUser>(api.patch<ApiEnvelope<AuthUser>>("/me", payload)),

	changePassword: (currentPassword: string, newPassword: string) =>
		api.post("/me/change-password", { currentPassword, newPassword }),

	listSessions: () =>
		unwrap<Session[]>(api.get<ApiEnvelope<Session[]>>("/me/sessions")),

	revokeSession: (id: string) =>
		unwrap<void>(api.delete<ApiEnvelope<void>>(`/me/sessions/${id}`)),

	revokeOtherSessions: () =>
		unwrap<{ revoked: number }>(
			api.post<ApiEnvelope<{ revoked: number }>>("/me/sessions/revoke-others"),
		),

	listMfa: () =>
		unwrap<MfaMethodFull[]>(api.get<ApiEnvelope<MfaMethodFull[]>>("/me/mfa")),

	setupTotp: () =>
		unwrap<TotpSetup>(api.post<ApiEnvelope<TotpSetup>>("/me/mfa/totp/setup")),

	verifyTotp: (setupId: string, code: string, label?: string) =>
		unwrap<{ method: { id: string; type: string }; recoveryCodes?: string[] }>(
			api.post<
				ApiEnvelope<{
					method: { id: string; type: string };
					recoveryCodes?: string[];
				}>
			>("/me/mfa/totp/verify", { setupId, code, label }),
		),

	deleteMfa: (id: string) =>
		unwrap<void>(api.delete<ApiEnvelope<void>>(`/me/mfa/${id}`)),

	setDefaultMfa: (id: string) =>
		unwrap<void>(api.post<ApiEnvelope<void>>(`/me/mfa/${id}/set-default`)),

	regenerateRecoveryCodes: () =>
		unwrap<{ codes: string[] }>(
			api.post<ApiEnvelope<{ codes: string[] }>>(
				"/me/recovery-codes/regenerate",
			),
		),
};

// re-export for convenience
export type { MfaMethod };
